import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiExternalLink, FiEye, FiMessageCircle, FiPlay, FiThumbsDown, FiThumbsUp } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Navbar from '../components/Navbar';
import { Seo } from '../components/Seo';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import wechatQRCode from '../assets/rgm-wechat-qr.png';
import wechatLogo from '../assets/wechat-logo.png';
import heyboxLogo from '../assets/heybox-logo.png';
import './OralHistory.css';

const mediaUrl = (value) => {
  if (!value || /^https?:\/\//i.test(value)) return value;
  return `${API_BASE_URL}${value}`;
};

const seoMediaUrl = (value) => {
  const resolved = mediaUrl(value);
  if (!resolved || /^https?:\/\//i.test(resolved)) return resolved;
  return `https://rgm.games${resolved.startsWith('/') ? resolved : `/${resolved}`}`;
};

const displayDate = (value) => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return `${year}年${Number(month)}月${Number(day)}日`;
};

const Stat = ({ icon, children }) => (
  <span className="oral-stat">{icon}{children}</span>
);

const LoadingState = ({ label = '正在载入…' }) => (
  <div className="oral-loading" role="status" aria-live="polite">
    <span className="oral-loading-mark" aria-hidden="true" />
    <p>{label}</p>
  </div>
);

const SocialFollow = () => (
  <aside className="oral-social" aria-labelledby="oral-social-title">
    <h2 id="oral-social-title">关注我们的社交媒体以在第一时间获取内容更新</h2>
    <div className="oral-social-platforms">
      <div className="oral-social-card oral-social-wechat">
        <div className="oral-social-copy">
          <img className="oral-social-logo oral-social-wechat-logo" src={wechatLogo} alt="微信" />
          <strong>RGM 微信公众号</strong>
          <span>长篇馆刊、游戏文化专题与项目动态</span>
        </div>
        <div className="oral-social-qr">
          <img src={wechatQRCode} alt="Retro Game Museum 微信公众号二维码" />
          <span>微信扫码关注</span>
        </div>
      </div>
      <div className="oral-social-card oral-social-heybox">
        <div className="oral-social-copy">
          <img className="oral-social-logo" src={heyboxLogo} alt="小黑盒" />
          <strong>小黑盒社区专栏</strong>
          <span>短篇观察、编辑推荐与网站进展</span>
        </div>
        <div className="oral-social-search" aria-label="请在小黑盒中搜索怀旧游戏博物馆">
          <span>怀旧游戏博物馆</span>
          <b>搜索</b>
        </div>
      </div>
    </div>
  </aside>
);

const ReferenceCard = ({ reference }) => {
  if (!reference) return null;
  if (reference.type === 'site') {
    return (
      <a className="oral-reference oral-reference-site" href={reference.url}>
        <div>
          <span className="oral-reference-kicker">原文所引内容</span>
          <h3>{reference.label}</h3>
        </div>
        <FiExternalLink aria-hidden="true" />
      </a>
    );
  }

  const isGame = reference.type === 'game';
  return (
    <a className={`oral-reference ${isGame ? 'oral-reference-game' : 'oral-reference-platform'}`} href={reference.url}>
      {reference.image && (
        <div className="oral-reference-media">
          <img
            src={mediaUrl(reference.image)}
            alt={reference.title || reference.label || ''}
            loading="lazy"
          />
        </div>
      )}
      <div className="oral-reference-copy">
        <span className="oral-reference-kicker">原文所引{isGame ? '游戏' : '展区'}</span>
        <h3>{reference.title || reference.label}</h3>
        {reference.subtitle && reference.subtitle !== reference.title && <p>{reference.subtitle}</p>}
        {isGame ? (
          <div className="oral-reference-meta">
            {[reference.platform, reference.year, reference.developer].filter(Boolean).map((item) => <span key={item}>{item}</span>)}
          </div>
        ) : reference.description ? <p className="oral-reference-description">{reference.description.slice(0, 110)}{reference.description.length > 110 ? '…' : ''}</p> : null}
        <span className="oral-reference-action">{isGame && <FiPlay aria-hidden="true" />}{isGame ? '现在游玩' : '进入展区'}</span>
      </div>
    </a>
  );
};

const ArticleList = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/oral-history')
      .then(({ data }) => setArticles(data))
      .catch(() => setError('文章列表暂时无法载入，请稍后再试。'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="oral-history-page">
      <Navbar />
      <header className="oral-history-hero">
        <div className="oral-history-hero-rule" />
        <h1>游戏口述史</h1>
        <p className="oral-history-intro">游戏口述史以个人视角记录人与游戏相遇、相伴与创造的故事，保存电子游戏背后真实而鲜活的记忆。馆藏心声邀请馆员分享自己的游戏经历，讲述那些陪伴成长的作品与难忘瞬间；游戏会客厅邀请游戏开发者及游戏社区中的参与者，分享他们与游戏结缘的经历、创作与贡献，讲述一款游戏从诞生到被玩家共同塑造的过程。</p>
        <div className="oral-history-editor">板块编辑：业羊</div>
      </header>

      <section className="oral-history-list" aria-label="游戏口述史文章">
        {loading && <LoadingState />}
        {error && <div className="oral-state">{error}</div>}
        {articles.map((article) => (
          <Link className="oral-article-card" to={`/oral-history/${article.slug}`} key={article.id}>
            <div className="oral-card-image">
              <img src={mediaUrl(article.cover_image)} alt={`${article.title}封面`} />
              <span>{article.series_name}</span>
            </div>
            <div className="oral-card-copy">
              <h2>{article.title}</h2>
              <p>{article.excerpt}</p>
              <div className="oral-card-footer">
                <span>{article.author}</span>
                <span>{displayDate(article.published_date)}</span>
                <span className="oral-card-stats"><FiEye /> {article.readers} <FiMessageCircle /> {article.comments}</span>
              </div>
            </div>
          </Link>
        ))}
      </section>
    </main>
  );
};

const ArticleReader = ({ slug }) => {
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [error, setError] = useState('');
  const [commentError, setCommentError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ authorName: '', content: '' });

  const loadComments = useCallback((articleId) => {
    apiClient.get(`/oral-history/${articleId}/comments`)
      .then(({ data }) => setComments(data))
      .catch(() => setCommentError('评论暂时无法载入。'));
  }, []);

  useEffect(() => {
    let active = true;
    apiClient.get(`/oral-history/${slug}`).then(async ({ data }) => {
      if (!active) return;
      setArticle(data);
      loadComments(data.id);
      if (!window.sessionStorage.getItem(`oral-history-view-${data.id}`)) {
        window.sessionStorage.setItem(`oral-history-view-${data.id}`, '1');
        try {
          const response = await apiClient.post(`/oral-history/${data.id}/view`);
          if (active) setArticle((current) => ({ ...current, ...response.data }));
        } catch {
          // Reading must remain available even if the counter cannot be recorded.
        }
      }
    }).catch(() => {
      if (active) setError('未能载入这篇口述史。');
    });
    return () => { active = false; };
  }, [slug, loadComments]);

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('rgmUser') || 'null');
      if (user) setForm((current) => ({ ...current, authorName: user.displayName || user.userName || '' }));
    } catch {
      // Anonymous comments remain available.
    }
  }, []);

  const vote = async (voteType) => {
    if (!article) return;
    try {
      const { data } = await apiClient.post(`/oral-history/${article.id}/vote`, { voteType });
      setArticle((current) => ({ ...current, ...data }));
    } catch {
      setError('投票暂时未能保存。');
    }
  };

  const submitComment = async (event) => {
    event.preventDefault();
    if (!form.authorName.trim() || !form.content.trim()) {
      setCommentError('请填写称呼和评论内容。');
      return;
    }
    setSubmitting(true);
    setCommentError('');
    try {
      const { data } = await apiClient.post(`/oral-history/${article.id}/comments`, form);
      setComments((current) => [...current, data]);
      setForm((current) => ({ ...current, content: '' }));
      setArticle((current) => ({ ...current, comments: current.comments + 1 }));
    } catch {
      setCommentError('评论暂时未能发布。');
    } finally {
      setSubmitting(false);
    }
  };

  const markdownComponents = useMemo(() => ({
    img: ({ src, alt }) => <img src={mediaUrl(src)} alt={alt || ''} loading="lazy" />,
    a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  }), []);

  if (error && !article) return <main className="oral-history-page"><Navbar /><div className="oral-state oral-reader-state">{error}<button onClick={() => navigate('/oral-history')}>返回</button></div></main>;
  if (!article) return <main className="oral-history-page oral-loading-page"><Navbar /><LoadingState label="正在载入文章…" /></main>;

  const canonicalUrl = `https://rgm.games/oral-history/${article.slug}`;
  const articleImage = seoMediaUrl(article.cover_image);
  const seoDescription = String(article.excerpt || '').replace(/\s+/g, ' ').trim().slice(0, 180);

  return (
    <main className="oral-history-page oral-reader-page">
      <Seo
        title={`${article.title} | 怀旧游戏博物馆`}
        description={seoDescription}
        canonical={canonicalUrl}
        image={articleImage}
        type="article"
        keywords={`${article.series_name},游戏口述史,${article.author || '玩家记忆'},中国游戏史,游戏文化`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            '@id': `${canonicalUrl}#article`,
            headline: article.title,
            description: seoDescription,
            image: articleImage,
            datePublished: article.published_date || undefined,
            dateModified: article.updated_at || article.published_date || undefined,
            articleSection: article.series_name,
            inLanguage: 'zh-CN',
            author: article.author ? { '@type': 'Person', name: article.author } : undefined,
            editor: article.editor ? { '@type': 'Person', name: article.editor } : undefined,
            publisher: {
              '@type': 'Organization',
              name: '怀旧游戏博物馆',
              url: 'https://rgm.games',
              logo: { '@type': 'ImageObject', url: 'https://rgm.games/icon512.png' },
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '怀旧游戏博物馆', item: 'https://rgm.games/' },
              { '@type': 'ListItem', position: 2, name: '电子游戏档案室', item: 'https://rgm.games/archives' },
              { '@type': 'ListItem', position: 3, name: '游戏口述史', item: 'https://rgm.games/oral-history' },
              { '@type': 'ListItem', position: 4, name: article.title, item: canonicalUrl },
            ],
          },
        ]}
      />
      <Navbar />
      <article className="oral-reader">
        <Link className="oral-back" to="/oral-history"><FiArrowLeft /> 返回游戏口述史</Link>
        <div className="oral-reader-series">{article.series_name}</div>
        <h1>{article.title}</h1>
        <div className="oral-reader-byline">
          <span>{article.author}</span><span>{displayDate(article.published_date)}</span><span>编辑：{article.editor}</span>
        </div>
        <a className="oral-original-link" href={article.original_url} target="_blank" rel="noopener noreferrer">
          微信公众号原文 <FiExternalLink />
        </a>

        <div className="oral-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{article.content_markdown}</ReactMarkdown>
        </div>

        <ReferenceCard reference={article.reference} />

        <div className="oral-engagement" aria-label="文章数据与投票">
          <button className={article.userVote === 'upvote' ? 'active' : ''} onClick={() => vote('upvote')}><FiThumbsUp /> {article.upvotes}</button>
          <button className={article.userVote === 'downvote' ? 'active' : ''} onClick={() => vote('downvote')}><FiThumbsDown /> {article.downvotes}</button>
          <Stat icon={<FiEye />} >{article.readers} 人阅读</Stat>
          <Stat icon={<FiMessageCircle />} >{article.comments} 条评论</Stat>
        </div>

        <section className="oral-comments">
          <h2>评论</h2>
          <form onSubmit={submitComment}>
            <input
              value={form.authorName}
              onChange={(event) => setForm({ ...form, authorName: event.target.value })}
              placeholder="您的称呼"
              maxLength={80}
              aria-label="您的称呼"
            />
            <textarea
              value={form.content}
              onChange={(event) => setForm({ ...form, content: event.target.value })}
              placeholder="写下你的回应…"
              maxLength={2000}
              rows={4}
              aria-label="评论内容"
            />
            <div className="oral-comment-submit">
              {commentError && <span>{commentError}</span>}
              <button type="submit" disabled={submitting}>{submitting ? '发布中…' : '发布评论'}</button>
            </div>
          </form>
          <div className="oral-comment-list">
            {!comments.length && !commentError && <p className="oral-no-comments">还没有评论。</p>}
            {comments.map((comment) => (
              <div className="oral-comment" key={comment.id}>
                <div><strong>{comment.author_name}</strong><time>{comment.created_at}</time></div>
                <p>{comment.content}</p>
              </div>
            ))}
          </div>
        </section>

        <SocialFollow />
      </article>
    </main>
  );
};

const OralHistory = () => {
  const { slug } = useParams();
  return slug ? <ArticleReader slug={slug} /> : <ArticleList />;
};

export default OralHistory;
